// src/components/panels/MoveHistoryPanel.tsx (UPDATED - WITH NAVIGATION FIX)
import React, { useState, useRef, useEffect } from "react";
import { Move } from "../../utils/MoveHistoryManager.ts";
import { PIECE_DEFINITIONS } from "../../data/pieceDefinitions.ts";
import { useAuth } from "../../context/AuthContext.tsx";
import {
  saveMoveHistory,
  getMoveHistories,
  deleteMoveHistory,
  SavedMoveHistory,
} from "../../api/moveHistoryService.ts";
import { SaveHistoryDialog, LoadHistoryDialog } from "../modals/MoveHistoryModals.tsx";

interface MoveHistoryPanelProps {
  moveHistory: Array<Move & { moveNumber: string }>;
  gameStatus: "active" | "check" | "checkmate" | "stalemate";
  currentTurn: "white" | "black";
  historyPosition: number;
  isReviewingHistory: boolean;
  halfmoveClock: number;
  fiftyMoveLimit: number;
  seventyFiveMoveLimit: number;
  goBackward: () => void;
  goForward: () => void;
  goToCurrentPosition: () => void;
  claim50MoveDraw: () => void;
  currentGameId: string;
  boardRows: number;
  onLoadMoveHistory?: (savedGame: SavedMoveHistory) => void;
  
  // Existing props for current game state
  pieces: Map<string, any>;
  capturedPieces: any[];
  
  // ⭐ NEW: Initial board state for navigation
  initialPieces: Map<string, any>;
}

export const MoveHistoryPanel: React.FC<MoveHistoryPanelProps> = ({
  moveHistory,
  gameStatus,
  currentTurn,
  historyPosition,
  isReviewingHistory,
  halfmoveClock,
  fiftyMoveLimit,
  seventyFiveMoveLimit,
  goBackward,
  goForward,
  goToCurrentPosition,
  claim50MoveDraw,
  currentGameId,
  boardRows,
  onLoadMoveHistory,
  pieces,
  capturedPieces,
  initialPieces,  // ⭐ NEW PROP
}) => {
  const moveListRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // UI State
  const [showTimestamps, setShowTimestamps] = useState<boolean>(false);
  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);
  const [showLoadDialog, setShowLoadDialog] = useState<boolean>(false);

  // Save/Load State
  const [saveName, setSaveName] = useState<string>("");
  const [savedHistories, setSavedHistories] = useState<SavedMoveHistory[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  // Modal States for notifications and confirmations
  const [notificationModal, setNotificationModal] = useState<{
    show: boolean;
    type: "success" | "error" | "info";
    title: string;
    message: string;
  }>({ show: false, type: "info", title: "", message: "" });

  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    show: boolean;
    historyId: number | null;
    historyName: string;
  }>({ show: false, historyId: null, historyName: "" });

  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);

  // Auto-scroll to bottom when moveHistory changes
  useEffect(() => {
    if (moveListRef.current && !isReviewingHistory) {
      moveListRef.current.scrollTop = moveListRef.current.scrollHeight;
    }
  }, [moveHistory, isReviewingHistory]);

  // Load saved histories when load dialog opens
  useEffect(() => {
    if (showLoadDialog && user) {
      loadSavedHistories();
    }
  }, [showLoadDialog, user]);

  const loadSavedHistories = async () => {
    console.log("=== loadSavedHistories called ===");
    try {
      setIsLoading(true);
      const histories = await getMoveHistories();
      console.log("Loaded histories:", histories.length);
      setSavedHistories(histories);
    } catch (error: any) {
      console.error("Error loading histories:", error);
      setErrorMessage(error.response?.data?.error || error.message || "Failed to load histories");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveHistory = async () => {
    console.log("=== handleSaveHistory called (WITH INITIAL STATE) ===");
    console.log("User:", user);
    console.log("Save name:", saveName);
    console.log("Move history length:", moveHistory.length);
    console.log("Current game ID:", currentGameId);
    console.log("Board rows:", boardRows);
    console.log("Initial pieces count:", initialPieces.size);  // ⭐ NEW LOG
    console.log("Current pieces count:", pieces.size);
    console.log("Current turn:", currentTurn);
    console.log("Game status:", gameStatus);
    console.log("Captured pieces count:", capturedPieces.length);
    console.log("Halfmove clock:", halfmoveClock);

    if (!user) {
      const errorMsg = "Please log in to save move history";
      console.error(errorMsg);
      setErrorMessage(errorMsg);
      setShowLoginModal(true);
      return;
    }

    if (!saveName.trim()) {
      const errorMsg = "Please enter a name for this game";
      console.error(errorMsg);
      setErrorMessage(errorMsg);
      return;
    }

    if (moveHistory.length === 0) {
      const errorMsg = "No moves to save";
      console.error(errorMsg);
      setErrorMessage(errorMsg);
      return;
    }

    try {
      setIsSaving(true);
      setErrorMessage("");

      console.log("Calling saveMoveHistory with complete game state:", {
        name: saveName.trim(),
        gamePreset: currentGameId,
        boardSize: boardRows,
        movesCount: moveHistory.length,
        initialPiecesCount: initialPieces.size,  // ⭐ NEW
        currentPiecesCount: pieces.size,
        currentTurn,
        gameStatus,
        capturedPiecesCount: capturedPieces.length,
        halfmoveClock,
      });

      const result = await saveMoveHistory(
        saveName.trim(),
        currentGameId,
        boardRows,
        moveHistory,
        initialPieces,  // ⭐ NEW PARAMETER - Initial board state for navigation
        pieces,         // Current board state
        currentTurn,
        gameStatus,
        capturedPieces,
        halfmoveClock
      );

      console.log("✅ Save successful! Result:", result);
      setSaveSuccess(true);
      setSaveName("");

    } catch (error: any) {
      console.error("=== Error saving history ===");
      console.error("Error object:", error);
      console.error("Error message:", error.message);
      console.error("Error response:", error.response);

      const errorMsg = error.response?.data?.error || error.message || "Failed to save move history";
      setErrorMessage(errorMsg);
    } finally {
      setIsSaving(false);
      console.log("=== handleSaveHistory finished ===");
    }
  };

  const handleLoadHistory = (savedGame: SavedMoveHistory) => {
    console.log("=== handleLoadHistory called ===");
    console.log("Saved game:", savedGame);

    if (onLoadMoveHistory) {
      onLoadMoveHistory(savedGame);
      setShowLoadDialog(false);
    } else {
      console.error("onLoadMoveHistory callback not provided");
      setNotificationModal({
        show: true,
        type: "error",
        title: "Load Error",
        message: "Cannot load history - callback not provided",
      });
    }
  };

  const handleDeleteHistory = (historyId: number, historyName: string) => {
    setDeleteConfirmModal({
      show: true,
      historyId,
      historyName,
    });
  };

  const confirmDeleteHistory = async () => {
    const { historyId, historyName } = deleteConfirmModal;
    if (!historyId) return;

    setDeleteConfirmModal({ show: false, historyId: null, historyName: "" });

    console.log("Deleting history:", historyId, historyName);
    try {
      await deleteMoveHistory(historyId);
      setSavedHistories(savedHistories.filter((h) => h.id !== historyId));
      setNotificationModal({
        show: true,
        type: "success",
        title: "Deleted",
        message: `"${historyName}" deleted successfully`,
      });
    } catch (error: any) {
      console.error("Error deleting history:", error);
      setNotificationModal({
        show: true,
        type: "error",
        title: "Delete Failed",
        message: error.message || "Failed to delete history",
      });
    }
  };

  const handleCloseSaveDialog = () => {
    setShowSaveDialog(false);
    setErrorMessage("");
    setSaveName("");
    setSaveSuccess(false);
  };

  const handleCloseLoadDialog = () => {
    setShowLoadDialog(false);
    setErrorMessage("");
  };

  const formatTimestamp = (timestamp: Date | string): string => {
    const moveTime = new Date(timestamp);
    return moveTime.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const handleExportHistory = () => {
    if (moveHistory.length === 0) return;

    // Build export data with AI heuristics context for verification
    const exportData = {
      exportDate: new Date().toISOString(),
      gamePreset: currentGameId,
      boardSize: boardRows,
      totalMoves: moveHistory.length,
      gameStatus,
      currentTurn,
      moves: moveHistory.map((move, index) => ({
        moveNumber: formatMoveNumber(move.moveNumber, move.pieceColor),
        piece: getPieceAbbreviation(move),
        from: positionToAlgebraic(move.from.row, move.from.col),
        to: positionToAlgebraic(move.to.row, move.to.col),
        color: move.pieceColor,
        captured: move.capturedPiece ? {
          type: move.capturedPiece.type,
          value: move.capturedPiece.value
        } : null,
        isCheck: move.isCheck || false,
        isCheckmate: move.isCheckmate || false,
        promotedTo: move.promotedTo || null,
        timestamp: move.timestamp ? new Date(move.timestamp).toISOString() : null,
        // AI heuristics context (only present for AI moves, used for verification)
        aiContext: move.aiContext ? {
          gamePhase: move.aiContext.game_phase,
          situation: move.aiContext.situation,
          isEmergency: move.aiContext.is_emergency,
          primaryGoal: move.aiContext.primary_goal,
          goals: move.aiContext.goals,
          profile: move.aiContext.profile
        } : null
      }))
    };

    // Create downloadable file
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chess-game-${currentGameId}-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatRelativeTime = (timestamp: Date | string): string => {
    const now = new Date();
    const moveTime = new Date(timestamp);
    const diffMs = now.getTime() - moveTime.getTime();
    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs < 60) {
      return `${diffSecs}s ago`;
    } else if (diffSecs < 3600) {
      const mins = Math.floor(diffSecs / 60);
      return `${mins}m ago`;
    } else if (diffSecs < 86400) {
      const hours = Math.floor(diffSecs / 3600);
      return `${hours}h ago`;
    } else {
      return moveTime.toLocaleDateString();
    }
  };

  const positionToAlgebraic = React.useCallback(
    (row: number, col: number): string => {
      if (
        !boardRows ||
        typeof boardRows !== "number" ||
        isNaN(boardRows) ||
        boardRows < 8
      ) {
        console.error("positionToAlgebraic: Invalid or missing boardRows", {
          boardRows,
          type: typeof boardRows,
          row,
          col,
        });
        return "??";
      }

      if (typeof row !== "number" || typeof col !== "number") {
        console.error("positionToAlgebraic: Invalid input types", {
          row,
          col,
          boardRows,
        });
        return "??";
      }

      if (isNaN(row) || isNaN(col)) {
        console.error("positionToAlgebraic: NaN values", {
          row,
          col,
          boardRows,
        });
        return "??";
      }

      if (row < 0 || row >= boardRows || col < 0 || col >= boardRows) {
        console.error("positionToAlgebraic: Out of bounds", {
          row,
          col,
          boardRows,
        });
        return "??";
      }

      const files = "abcdefghijklmnopqrstuvwxyz";
      const file = files[col];
      if (!file) {
        console.error("positionToAlgebraic: Invalid column", {
          col,
          maxCols: files.length,
          boardRows,
        });
        return "??";
      }

      const rank = boardRows - row;

      if (isNaN(rank) || rank < 1 || rank > boardRows) {
        console.error("positionToAlgebraic: Invalid rank", {
          boardRows,
          row,
          rank,
        });
        return "??";
      }

      return `${file}${rank}`;
    },
    [boardRows]
  );

  const formatMoveNumber = (
    moveNum: string,
    color: "white" | "black"
  ): string => {
    const num = moveNum.replace(/\D/g, "");
    return `${num}${color === "white" ? "W" : "B"}`;
  };

  const getPieceAbbreviation = (
    move: Move & { moveNumber: string }
  ): string => {
    if (move.piece?.abbreviation) {
      return move.piece.abbreviation.toUpperCase();
    }

    if (move.pieceType) {
      const pieceType = move.pieceType.toLowerCase();
      const pieceDefinition = PIECE_DEFINITIONS[pieceType];
      if (pieceDefinition?.abbreviation) {
        return pieceDefinition.abbreviation.toUpperCase();
      }
    }

    return move.pieceType?.substring(0, 3).toUpperCase() || "???";
  };

  return (
    <>
      <div
        className="move-history-panel"
        style={{
          backgroundColor: "#fff",
          border: "2px solid #333",
          borderRadius: "8px",
          padding: "15px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <h3
          style={{
            margin: "0 0 5px 0",
            fontSize: "14px",
            color: "#333",
            textAlign: "center",
            borderBottom: "2px solid #4CAF50",
            paddingBottom: "5px",
          }}
        >
          Move History
        </h3>

        {/* Save/Load/Export Controls */}
        <div style={{ display: "flex", gap: "5px", marginBottom: "5px" }}>
          <button
            onClick={() => {
              console.log("Save button clicked", { user, moveHistoryLength: moveHistory.length });
              setShowSaveDialog(true);
            }}
            disabled={moveHistory.length === 0 || !user}
            style={{
              flex: 1,
              padding: "6px 10px",
              backgroundColor:
                moveHistory.length > 0 && user ? "#4CAF50" : "#ccc",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor:
                moveHistory.length > 0 && user ? "pointer" : "not-allowed",
              fontSize: "12px",
              fontWeight: "bold",
            }}
            title={!user ? "Login to save" : "Save current game"}
          >
            💾 Save
          </button>
          <button
            onClick={() => {
              console.log("Load button clicked", { user });
              setShowLoadDialog(true);
            }}
            disabled={!user}
            style={{
              flex: 1,
              padding: "6px 10px",
              backgroundColor: user ? "#2196F3" : "#ccc",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: user ? "pointer" : "not-allowed",
              fontSize: "12px",
              fontWeight: "bold",
            }}
            title={!user ? "Login to load" : "Load saved game"}
          >
            📁 Load
          </button>
          <button
            onClick={handleExportHistory}
            disabled={moveHistory.length === 0}
            style={{
              flex: 1,
              padding: "6px 10px",
              backgroundColor: moveHistory.length > 0 ? "#FF9800" : "#ccc",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: moveHistory.length > 0 ? "pointer" : "not-allowed",
              fontSize: "12px",
              fontWeight: "bold",
            }}
            title="Export game history to file"
          >
            📤 Export
          </button>
        </div>

        {/* User Login Status Indicator */}
        {!user && (
          <div
            style={{
              marginBottom: "5px",
              padding: "5px",
              backgroundColor: "#fff3cd",
              borderRadius: "4px",
              border: "1px solid #ffc107",
              textAlign: "center",
              fontSize: "11px",
              color: "#856404",
            }}
          >
            ⚠️ Login required to save/load games
          </div>
        )}

        {/* Timestamp Toggle */}
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            cursor: "pointer",
            marginBottom: "5px",
            fontSize: "12px",
          }}
        >
          <input
            type="checkbox"
            checked={showTimestamps}
            onChange={(e) => setShowTimestamps(e.target.checked)}
            style={{ width: "16px", height: "16px", cursor: "pointer" }}
          />
          <span>Show Timestamps</span>
        </label>

        {/* Navigation Controls */}
        <div
          style={{
            display: "flex",
            gap: "5px",
            marginBottom: "5px",
            justifyContent: "center",
          }}
        >
          <button
            onClick={goBackward}
            disabled={historyPosition < 0}
            style={{
              padding: "8px 12px",
              backgroundColor: historyPosition >= 0 ? "#2196F3" : "#ccc",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: historyPosition >= 0 ? "pointer" : "not-allowed",
              fontSize: "16px",
              fontWeight: "bold",
            }}
            title="Previous move"
          >
            ◀
          </button>

          <button
            onClick={goForward}
            disabled={historyPosition >= moveHistory.length - 1}
            style={{
              padding: "8px 12px",
              backgroundColor:
                historyPosition < moveHistory.length - 1 ? "#2196F3" : "#ccc",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor:
                historyPosition < moveHistory.length - 1
                  ? "pointer"
                  : "not-allowed",
              fontSize: "16px",
              fontWeight: "bold",
            }}
            title="Next move"
          >
            ▶
          </button>

          {isReviewingHistory && (
            <button
              onClick={goToCurrentPosition}
              style={{
                padding: "8px 12px",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "11px",
                fontWeight: "bold",
              }}
              title="Go to current position"
            >
              Go to Current
            </button>
          )}
        </div>

        {/* Game Status Display with Flashing Animation */}
        <style>{`
          @keyframes turnFlash {
            0%, 100% {
              background-color: #e1bee7;
              border-color: #ce93d8;
            }
            50% {
              background-color: #b3e5fc;
              border-color: #81d4fa;
            }
          }
        `}</style>
        <div
          style={{
            marginBottom: "5px",
            padding: "5px",
            backgroundColor:
              gameStatus === "checkmate"
                ? "#d32f2f"
                : gameStatus === "stalemate"
                ? "#ff9800"
                : gameStatus === "check"
                ? "#ffc107"
                : "#e1bee7",
            color:
              gameStatus === "checkmate" || gameStatus === "stalemate"
                ? "#fff"
                : gameStatus === "check"
                ? "#000"
                : "#333",
            borderRadius: "4px",
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "20px",
            border: "2px solid",
            borderColor:
              gameStatus === "checkmate"
                ? "#b71c1c"
                : gameStatus === "stalemate"
                ? "#f57c00"
                : gameStatus === "check"
                ? "#ffa000"
                : "#ce93d8",
            animation: gameStatus === "active" ? "turnFlash 2s ease-in-out infinite" : "none",
          }}
        >
          {gameStatus === "checkmate" &&
            `Checkmate! ${currentTurn === "white" ? "Black" : "White"} Wins!`}
          {gameStatus === "stalemate" && "Stalemate! Game Draw"}
          {gameStatus === "check" &&
            `Check! ${currentTurn === "white" ? "White" : "Black"}'s Turn`}
          {gameStatus === "active" &&
            `${currentTurn === "white" ? "White" : "Black"}'s Turn`}
        </div>

        {/* 50-Move Rule Warning */}
        {halfmoveClock >= fiftyMoveLimit * 2 &&
          halfmoveClock < seventyFiveMoveLimit * 2 &&
          gameStatus === "active" && (
            <div
              style={{
                marginBottom: "5px",
                padding: "10px",
                backgroundColor: "#fff3cd",
                borderRadius: "4px",
                border: "2px solid #ffc107",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontWeight: "bold",
                  color: "#856404",
                  fontSize: "16px",
                  marginBottom: "5px",
                }}
              >
                ⚠️ {fiftyMoveLimit}-Move Rule Active
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#856404",
                  marginBottom: "5px",
                }}
              >
                No capture or pawn move in {Math.floor(halfmoveClock / 2)} moves
                {halfmoveClock >= seventyFiveMoveLimit * 2 - 20 && (
                  <div
                    style={{
                      color: "#d32f2f",
                      fontWeight: "bold",
                      marginTop: "4px",
                    }}
                  >
                    ⚠️ Automatic draw in{" "}
                    {seventyFiveMoveLimit - Math.floor(halfmoveClock / 2)}{" "}
                    moves!
                  </div>
                )}
              </div>
              <button
                onClick={claim50MoveDraw}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                Claim Draw
              </button>
            </div>
          )}

        {/* Total Moves Counter */}
        <div
          style={{
            marginBottom: "5px",
            padding: "6px",
            backgroundColor: "#e0e0e0",
            borderRadius: "4px",
            textAlign: "center",
            fontSize: "12px",
            color: "#666",
          }}
        >
          Total Moves: {moveHistory.length}
        </div>

        {/* Move List */}
        {moveHistory.length === 0 ? (
          <p
            style={{
              color: "#666",
              fontStyle: "italic",
              textAlign: "center",
              fontSize: "12px",
            }}
          >
            No moves yet
          </p>
        ) : (
          <div
            ref={moveListRef}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            {moveHistory.map((move, index) => (
              <div
                key={move.id}
                style={{
                  padding: "8px",
                  backgroundColor:
                    index === historyPosition
                      ? "#b3e5fc"
                      : move.pieceColor === "white"
                      ? "#fff"
                      : "#e0e0e0",
                  borderRadius: "4px",
                  border:
                    index === historyPosition
                      ? "2px solid #0288d1"
                      : "1px solid #ddd",
                  fontSize: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "2px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <strong
                      style={{
                        fontSize: "13px",
                        minWidth: "32px",
                        color:
                          move.pieceColor === "white" ? "#1976d2" : "#d32f2f",
                      }}
                    >
                      {formatMoveNumber(move.moveNumber, move.pieceColor)}
                    </strong>
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: move.isCheckmate
                          ? "#d32f2f"
                          : move.isCheck
                          ? "#f57c00"
                          : "#333",
                      }}
                    >
                      {getPieceAbbreviation(move)}{" "}
                      {positionToAlgebraic(move.from.row, move.from.col)}-
                      {positionToAlgebraic(move.to.row, move.to.col)}
                      {move.promotedTo &&
                        `=${move.promotedTo.charAt(0).toUpperCase()}`}
                      {move.isCheckmate ? "#" : move.isCheck ? "+" : ""}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {(move.isCheck || move.isCheckmate) && (
                      <span
                        style={{
                          fontSize: "14px",
                          color: move.isCheckmate ? "#d32f2f" : "#f57c00",
                          fontWeight: "bold",
                        }}
                      >
                        {move.isCheckmate ? "‡" : "†"}
                      </span>
                    )}
                    <span
                      style={{
                        color: move.pieceColor === "white" ? "#000" : "#333",
                        fontWeight: "bold",
                      }}
                    >
                      {move.pieceColor === "white" ? "⚪" : "⚫"}
                    </span>
                  </div>
                </div>

                {showTimestamps && move.timestamp && (
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#666",
                      marginTop: "3px",
                      fontFamily: "monospace",
                    }}
                  >
                    🕐 {formatTimestamp(move.timestamp)}
                    <span style={{ marginLeft: "8px", fontStyle: "italic" }}>
                      ({formatRelativeTime(move.timestamp)})
                    </span>
                  </div>
                )}

                {move.promotedTo && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#4CAF50",
                      marginTop: "2px",
                      fontWeight: "bold",
                    }}
                  >
                    Promoted to{" "}
                    {move.promotedTo.charAt(0).toUpperCase() +
                      move.promotedTo.slice(1)}
                  </div>
                )}
                {move.capturedPiece && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#d32f2f",
                      marginTop: "2px",
                    }}
                  >
                    Captured: {move.capturedPiece.type} (
                    {move.capturedPiece.value?.toFixed(2) || "0.00"})
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <SaveHistoryDialog
        show={showSaveDialog}
        saveName={saveName}
        setSaveName={setSaveName}
        errorMessage={errorMessage}
        isSaving={isSaving}
        moveHistoryLength={moveHistory.length}
        currentGameId={currentGameId}
        boardRows={boardRows}
        saveSuccess={saveSuccess}
        onSave={handleSaveHistory}
        onClose={handleCloseSaveDialog}
      />

      <LoadHistoryDialog
        show={showLoadDialog}
        savedHistories={savedHistories}
        errorMessage={errorMessage}
        isLoading={isLoading}
        onLoad={handleLoadHistory}
        onDelete={handleDeleteHistory}
        onClose={handleCloseLoadDialog}
      />

      {/* Login Required Modal */}
      {showLoginModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
          onClick={() => setShowLoginModal(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "25px",
              maxWidth: "350px",
              width: "90%",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, color: "#333", textAlign: "center" }}>
              Login Required
            </h3>
            <p style={{ textAlign: "center", color: "#666", marginBottom: "20px" }}>
              Please log in to save move history.
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                onClick={() => setShowLoginModal(false)}
                style={{
                  padding: "10px 30px",
                  backgroundColor: "#2196F3",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2001,
          }}
          onClick={() => setDeleteConfirmModal({ show: false, historyId: null, historyName: "" })}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "25px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, color: "#333", textAlign: "center" }}>
              Delete Move History
            </h3>
            <p style={{ textAlign: "center", color: "#333", marginBottom: "10px" }}>
              Are you sure you want to delete "{deleteConfirmModal.historyName}"?
            </p>
            <p style={{ textAlign: "center", color: "#c62828", fontWeight: 600, marginBottom: "20px" }}>
              This action cannot be undone.
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button
                onClick={() => setDeleteConfirmModal({ show: false, historyId: null, historyName: "" })}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#e0e0e0",
                  color: "#333",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteHistory}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#f44336",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notificationModal.show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2002,
          }}
          onClick={() => setNotificationModal({ show: false, type: "info", title: "", message: "" })}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "25px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
              borderTop: `4px solid ${
                notificationModal.type === "success"
                  ? "#4CAF50"
                  : notificationModal.type === "error"
                  ? "#f44336"
                  : "#2196F3"
              }`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                marginTop: 0,
                textAlign: "center",
                color:
                  notificationModal.type === "success"
                    ? "#4CAF50"
                    : notificationModal.type === "error"
                    ? "#f44336"
                    : "#2196F3",
              }}
            >
              {notificationModal.title}
            </h3>
            <p style={{ textAlign: "center", color: "#333", marginBottom: "20px" }}>
              {notificationModal.message}
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                onClick={() => setNotificationModal({ show: false, type: "info", title: "", message: "" })}
                style={{
                  padding: "10px 30px",
                  backgroundColor:
                    notificationModal.type === "success"
                      ? "#4CAF50"
                      : notificationModal.type === "error"
                      ? "#f44336"
                      : "#2196F3",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
